import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';

import { createApiKey, getApiKeys, revokeApiKey } from 'Services/apiKeyService';
import useCan from 'hooks/useCan';
import { CAPABILITIES } from 'utils/capabilities';
import { GENERIC_ERROR_MESSAGE } from 'utils/constants';

import ApiDocsPanel from './ApiDocsPanel';
import ApiKeyList from './ApiKeyList';
import CreateApiKeyForm from './CreateApiKeyForm';
import NewKeyModal from './NewKeyModal';
import './apiKeys.css';

const errorFrom = (error) =>
  error?.response?.data?.message || error?.message || GENERIC_ERROR_MESSAGE;

const ApiKeys = () => {
  const { allowed: canManageKeys, isResolved } = useCan(CAPABILITIES.APIKEYS_MANAGE);
  const [messageApi, contextHolder] = message.useMessage();

  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getApiKeys();
      setApiKeys(response?.data?.data || []);
    } catch (error) {
      messageApi.open({ type: 'error', content: errorFrom(error) });
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    if (canManageKeys) {
      loadKeys();
    } else {
      setLoading(false);
    }
  }, [canManageKeys, loadKeys]);

  const handleCreate = async (name) => {
    try {
      const response = await createApiKey({ name });
      const created = response?.data?.data;
      // The plaintext secret is only present on this response.
      setNewKey(created);
      await loadKeys();
      return true;
    } catch (error) {
      messageApi.open({ type: 'error', content: errorFrom(error) });
      return false;
    }
  };

  const handleRevoke = async (apiKey) => {
    try {
      await revokeApiKey(apiKey.id);
      messageApi.open({ type: 'success', content: `"${apiKey.name}" revoked.` });
      await loadKeys();
    } catch (error) {
      messageApi.open({ type: 'error', content: errorFrom(error) });
    }
  };

  if (!isResolved || !canManageKeys) {
    return null;
  }

  return (
    <>
      {contextHolder}

      <div className="card cardc p-5">
        <div className="h5">API Keys</div>
        <p className="text-muted">
          Create a key to pull your store's demo data — sentiment scores,
          meeting takeaways, pros and cons, and commissions — into your own
          tools.
        </p>
        <hr />

        <CreateApiKeyForm onCreate={handleCreate} />
      </div>

      <div className="card cardc p-5 mt-4">
        <div className="h5">Your keys</div>
        <hr />
        <ApiKeyList apiKeys={apiKeys} loading={loading} onRevoke={handleRevoke} />
      </div>

      <div className="card cardc p-5 mt-4">
        <div className="h5">Documentation</div>
        <hr />
        <ApiDocsPanel />
      </div>

      <NewKeyModal
        open={newKey !== null}
        apiKey={newKey}
        onClose={() => setNewKey(null)}
      />
    </>
  );
};

export default ApiKeys;
